import { useEffect, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats as F } from "html5-qrcode";
import { Camera, CameraOff, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { scanFeedback } from "@/lib/feedback";

const FORMATS = [F.EAN_13, F.EAN_8, F.CODE_128, F.CODE_39, F.QR_CODE, F.UPC_A, F.UPC_E];
const REGION = "sk-camera-region";

function cameraError(e) {
  const s = String(e?.name || e?.message || e || "");
  if (!window.isSecureContext) return "La caméra nécessite une connexion sécurisée (HTTPS).";
  if (/NotAllowed|Permission|denied/i.test(s)) return "Accès à la caméra refusé. Autorisez la caméra dans les réglages du navigateur, puis réessayez.";
  if (/NotFound|not found|no camera|Overconstrained/i.test(s)) return "Aucune caméra détectée sur cet appareil.";
  if (/NotReadable|TrackStart|in use/i.test(s)) return "La caméra est déjà utilisée par une autre application.";
  return `Impossible de démarrer la caméra : ${s}`;
}

function CameraView({ onDetected, onError }) {
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onError(window.isSecureContext ? "Ce navigateur ne permet pas l'accès à la caméra." : cameraError());
      return undefined;
    }
    let stopped = false;
    let done = false;
    let scanner;
    let started = Promise.resolve();
    const swallow = (ev) => { if (/play\(\) request was interrupted/i.test(String(ev.reason?.message || ev.reason))) ev.preventDefault(); };
    window.addEventListener("unhandledrejection", swallow);
    const timer = setTimeout(() => {
      scanner = new Html5Qrcode(REGION, { formatsToSupport: FORMATS, verbose: false, experimentalFeatures: { useBarCodeDetectorIfSupported: true } });
      started = scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: (w, h) => ({ width: Math.max(120, Math.floor(w * 0.85)), height: Math.max(80, Math.floor(h * 0.5)) }) },
        (text) => { if (!done) { done = true; onDetected(text); } },
        () => {}
      );
      started.catch((e) => { if (!stopped) onError(cameraError(e)); });
    }, 150);
    return () => {
      stopped = true;
      clearTimeout(timer);
      if (scanner) started.then(() => scanner.stop()).then(() => scanner.clear()).catch(() => {});
      setTimeout(() => window.removeEventListener("unhandledrejection", swallow), 2000);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div id={REGION} className="min-h-[260px] w-full overflow-hidden rounded-md bg-slate-950" data-testid="camera-region" />;
}

export function CameraDialog({ open, onOpenChange, onDetected, title = "Scanner avec la caméra" }) {
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [manual, setManual] = useState("");

  useEffect(() => { if (open) { setError(""); setManual(""); } }, [open]);
  const finish = (code) => { onOpenChange(false); onDetected(code.trim()); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="camera-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{title}</DialogTitle>
          <DialogDescription>EAN-13, EAN-8, Code 128, Code 39, QR. Caméra arrière privilégiée.</DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="space-y-3 rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="camera-error">
            <div className="flex items-center gap-2 font-semibold"><CameraOff className="h-5 w-5" /> {error}</div>
            <Button type="button" variant="outline" className="h-11 gap-2" onClick={() => { setError(""); setAttempt((a) => a + 1); }} data-testid="camera-retry-button"><RotateCcw className="h-4 w-4" /> Réessayer</Button>
          </div>
        ) : open && (
          <CameraView key={attempt} onDetected={(t) => { scanFeedback(true, t); finish(t); }} onError={(m) => { scanFeedback(false, "Caméra indisponible"); setError(m); }} />
        )}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (manual.trim()) finish(manual); }}>
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ou saisir le code manuellement" className="h-12 font-mono" data-testid="camera-manual-input" />
          <Button type="submit" className="h-12" data-testid="camera-manual-submit">Valider</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CameraButton({ onDetected, testid = "camera-button", label = "Caméra", title, className = "", iconOnly }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" className={`h-12 gap-2 ${iconOnly ? "w-12 p-0" : ""} ${className}`} title="Scanner avec la caméra" onClick={() => setOpen(true)} data-testid={testid}>
        <Camera className="h-4 w-4" />{!iconOnly && label}
      </Button>
      <CameraDialog open={open} onOpenChange={setOpen} onDetected={onDetected} title={title} />
    </>
  );
}
